import React, { useEffect, useCallback } from 'react';
import { Button } from './Button';

const GoogleSignInButton = ({ onSuccess, onError }) => {
  const handleCredentialResponse = useCallback((response) => {
    if (response.credential) {
      onSuccess(response.credential);
    } else {
      onError('Google sign-in failed');
    }
  }, [onSuccess, onError]);

  useEffect(() => {
    // Load the Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      try {
        if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
          console.error('Google client ID is not set');
          return;
        }
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true
        });
      } catch (error) {
        onError('Failed to initialize Google Sign-in');
      }
    };
    script.onerror = (error) => {
      console.error('Error loading Google Sign-in script:', error);
      // onError('Failed to load Google Sign-in');
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      // Cleanup any Google Sign-In elements
      const googleSignInDiv = document.getElementById('g_id_onload');
      if (googleSignInDiv) {
        googleSignInDiv.remove();
      }
    };
  }, [handleCredentialResponse, onError]);

  const handleClick = () => {
    try {
      if (!window.google?.accounts?.id) {
        console.error('Google Sign-in not initialized');
        onError('Google Sign-in not initialized');
        return;
      }
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed()) {
          console.error('Google Sign-in prompt not displayed:', notification.getNotDisplayedReason());
        } else if (notification.isSkippedMoment()) {
          console.error('Google Sign-in prompt skipped:', notification.getSkippedReason());
        } else if (notification.isDismissedMoment()) {
          console.error('Google Sign-in prompt dismissed:', notification.getDismissedReason());
        }
      });
    } catch (error) {
      console.error('Error showing Google Sign-in prompt:', error);
      onError('Failed to show Google Sign-in prompt');
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full h-10 px-4 flex items-center justify-center gap-2 text-sm font-medium"
      onClick={handleClick}
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      Sign in with Google
    </Button>
  );
};

export default GoogleSignInButton; 